import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { WORKER_URL, getAuthHeaders } from '../../utils/api';

export default function UserProfile() {
  const { user, setUser } = useAuth();
  const [section, setSection] = useState(null);
  const [form, setForm] = useState({});
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [dueDate, setDueDate] = useState(null);
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(`${WORKER_URL}/api/user/subscription`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.dueDate) setDueDate(data.dueDate); })
      .catch(() => {});
    fetch(`${WORKER_URL}/api/referral/stats`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setReferral(data); })
      .catch(() => {});
  }, []);

  const copyReferralLink = () => {
    if (!referral?.code) return;
    navigator.clipboard.writeText(`https://integramkp.com.br/?ref=${referral.code}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openInvoices = async () => {
    setShowInvoices(true);
    setInvoicesLoading(true);
    try {
      const res = await fetch(`${WORKER_URL}/api/user/invoices`, { headers: getAuthHeaders() });
      if (res.ok) { const d = await res.json(); setInvoices(d.invoices || []); }
    } catch {}
    setInvoicesLoading(false);
  };

  const downloadReceipt = async (id) => {
    const token = localStorage.getItem('saas_token');
    const res = await fetch(`${WORKER_URL}/api/user/invoices/${id}/receipt?token=${token}`);
    const html = await res.text();
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fatura-${id}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadAll = async () => {
    for (const inv of invoices) {
      await downloadReceipt(inv.id);
    }
  };
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  const openSection = (s) => { setSection(s); setStep(1); setForm({}); setError(''); setSuccess(''); };
  const close = () => { setSection(null); setError(''); setSuccess(''); };

  const handleSaveName = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) { setError('Nome é obrigatório'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${WORKER_URL}/api/user/profile`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar');
      setUser(u => ({ ...u, name: form.name.trim() }));
      setSuccess('Nome atualizado!');
      setSection(null);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleSendEmailCode = async (e) => {
    e.preventDefault();
    if (!form.newEmail?.trim()) { setError('Email é obrigatório'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${WORKER_URL}/api/user/change-email/send-code`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ newEmail: form.newEmail.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao enviar código');
      setStep(2);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleConfirmEmail = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await fetch(`${WORKER_URL}/api/user/change-email/confirm`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ newEmail: form.newEmail.trim(), code: form.code })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao confirmar email');
      setUser(u => ({ ...u, email: form.newEmail.trim().toLowerCase() }));
      setSuccess('Email atualizado!');
      setSection(null);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (form.newPassword?.length < 6) { setError('Nova senha deve ter no mínimo 6 caracteres'); return; }
    if (form.newPassword !== form.confirmPassword) { setError('As senhas não coincidem'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${WORKER_URL}/api/user/change-password`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword: form.currentPassword, newPassword: form.newPassword })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao alterar senha');
      setSuccess('Senha alterada!');
      setSection(null);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const fields = [
    { key: 'name', label: 'Nome', value: user?.name },
    { key: 'email', label: 'Email', value: user?.email },
    { key: 'password', label: 'Senha', value: '••••••••' },
  ];

  return (
    <div style={{ padding: '32px', maxWidth: '520px' }}>
      <h2 style={{ color: '#2D3277', marginBottom: '6px' }}>Minha Conta</h2>
      <p style={{ color: '#999', fontSize: '0.85rem', marginBottom: '28px' }}>Gerencie suas informações pessoais</p>

      {success && (
        <div style={{ backgroundColor: '#f0fff4', border: '1px solid #9ae6b4', color: '#276749', padding: '10px 14px', borderRadius: '6px', fontSize: '0.85rem', marginBottom: '20px' }}>
          {success}
        </div>
      )}

      <div style={{ backgroundColor: '#f9f9f9', borderRadius: '10px', overflow: 'hidden' }}>
        {fields.map((field, i) => (
          <div key={field.key}>
            {/* Linha principal */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 18px',
              borderBottom: section === field.key ? '1px solid #e0e0e0' : (i < fields.length - 1 ? '1px solid #eee' : 'none')
            }}>
              <div>
                <span style={{ color: '#888', fontSize: '0.78rem', display: 'block', marginBottom: '2px' }}>{field.label}</span>
                <span style={{ color: '#222', fontSize: '0.9rem', fontWeight: 500 }}>{field.value}</span>
              </div>
              <button
                onClick={() => section === field.key ? close() : openSection(field.key)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2D3277', fontSize: '0.8rem', fontWeight: 500, padding: '4px 8px', borderRadius: '4px', opacity: 0.7 }}
              >
                {section === field.key ? 'cancelar' : 'alterar'}
              </button>
            </div>

            {/* Formulário inline */}
            {section === field.key && (
              <div style={{ padding: '16px 18px', backgroundColor: '#fff', borderBottom: i < fields.length - 1 ? '1px solid #eee' : 'none' }}>
                {error && (
                  <div style={{ backgroundColor: '#fff0f0', border: '1px solid #ffcccc', color: '#cc0000', padding: '8px 12px', borderRadius: '6px', fontSize: '0.82rem', marginBottom: '12px' }}>
                    {error}
                  </div>
                )}

                {field.key === 'name' && (
                  <form onSubmit={handleSaveName} style={{ display: 'flex', gap: '8px' }}>
                    <input type="text" defaultValue={user?.name} onChange={set('name')} required autoFocus style={inputStyle} placeholder="Novo nome" />
                    <button type="submit" disabled={loading} style={btnStyle}>{loading ? '...' : 'Salvar'}</button>
                  </form>
                )}

                {field.key === 'email' && step === 1 && (
                  <form onSubmit={handleSendEmailCode} style={{ display: 'flex', gap: '8px' }}>
                    <input type="email" value={form.newEmail || ''} onChange={set('newEmail')} required autoFocus style={inputStyle} placeholder="Novo email" />
                    <button type="submit" disabled={loading} style={btnStyle}>{loading ? '...' : 'Enviar código'}</button>
                  </form>
                )}

                {field.key === 'email' && step === 2 && (
                  <form onSubmit={handleConfirmEmail}>
                    <p style={{ fontSize: '0.82rem', color: '#555', marginBottom: '10px' }}>Código enviado para <strong>{form.newEmail}</strong></p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input type="text" value={form.code || ''} onChange={set('code')} placeholder="000000" maxLength={6} required autoFocus
                        style={{ ...inputStyle, letterSpacing: '6px', textAlign: 'center', fontSize: '1rem' }} />
                      <button type="submit" disabled={loading} style={btnStyle}>{loading ? '...' : 'Confirmar'}</button>
                    </div>
                  </form>
                )}

                {field.key === 'password' && (
                  <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <input type="password" value={form.currentPassword || ''} onChange={set('currentPassword')} placeholder="Senha atual" required autoFocus style={inputStyle} />
                    <input type="password" value={form.newPassword || ''} onChange={set('newPassword')} placeholder="Nova senha (mín. 6 caracteres)" minLength={6} required style={inputStyle} />
                    <input type="password" value={form.confirmPassword || ''} onChange={set('confirmPassword')} placeholder="Confirmar nova senha" required style={inputStyle} />
                    <button type="submit" disabled={loading} style={{ ...btnStyle, alignSelf: 'flex-end', marginTop: '4px' }}>{loading ? 'Aguarde...' : 'Alterar senha'}</button>
                  </form>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Indicações */}
      {referral && (
        <div style={{ marginTop: '28px' }}>
          <h3 style={{ color: '#2D3277', marginBottom: '4px', fontSize: '1rem' }}>Indicações</h3>
          <p style={{ color: '#999', fontSize: '0.82rem', marginBottom: '16px' }}>Indique amigos e ganhe 15% de desconto por cada um que pagar</p>

          {/* Link de indicação */}
          <div style={{ backgroundColor: '#f9f9f9', borderRadius: '10px', padding: '16px 18px', marginBottom: '12px' }}>
            <span style={{ color: '#888', fontSize: '0.78rem', display: 'block', marginBottom: '6px' }}>Seu link de indicação</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ flex: 1, fontSize: '0.85rem', color: '#444', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '7px 10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                integramkp.com.br/?ref={referral.code}
              </span>
              <button onClick={copyReferralLink} style={{ ...btnStyle, backgroundColor: copied ? '#16a34a' : '#2D3277', whiteSpace: 'nowrap', padding: '7px 14px', fontSize: '0.82rem' }}>
                {copied ? '✓ Copiado!' : 'Copiar link'}
              </button>
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '16px' }}>
            {[
              { label: 'Indicados', value: referral.referrals?.length || 0 },
              { label: 'Confirmados', value: referral.referrals?.filter(r => r.status === 'credited').length || 0 },
              { label: 'Desconto acumulado', value: `${referral.discountPct || 0}%` },
            ].map(s => (
              <div key={s.label} style={{ backgroundColor: '#f0f7ff', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#2D3277' }}>{s.value}</div>
                <div style={{ fontSize: '0.72rem', color: '#666', marginTop: '2px' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Crédito disponível */}
          {referral.discountPct > 0 && (
            <div style={{ backgroundColor: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: '8px', padding: '10px 14px', fontSize: '0.85rem', color: '#276749', marginBottom: '12px' }}>
              🎉 Você tem <strong>{referral.discountPct}% de desconto</strong> disponível na próxima fatura!
            </div>
          )}

          {/* Histórico */}
          {referral.referrals?.length > 0 && (
            <div style={{ backgroundColor: '#f9f9f9', borderRadius: '10px', overflow: 'hidden' }}>
              {referral.referrals.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: i < referral.referrals.length - 1 ? '1px solid #eee' : 'none' }}>
                  <span style={{ fontSize: '0.88rem', color: '#333' }}>{r.name}</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, padding: '3px 8px', borderRadius: '20px', backgroundColor: r.status === 'credited' ? '#f0fff4' : '#fffbeb', color: r.status === 'credited' ? '#16a34a' : '#b45309' }}>
                    {r.status === 'credited' ? '✓ Confirmado' : 'Aguardando pagamento'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Botão faturas + vencimento */}
      <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <button onClick={openInvoices} style={{ background: 'none', border: '1px solid #ddd', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer', color: '#2D3277', fontSize: '0.85rem', fontWeight: 500 }}>
          Ver histórico de faturas
        </button>
        {dueDate && (
          <span style={{ fontSize: '0.82rem', color: '#666' }}>
            Próximo vencimento: <strong style={{ color: '#2D3277' }}>{dueDate.split('-').reverse().join('/')}</strong>
          </span>
        )}
      </div>

      {/* Modal faturas */}
      {showInvoices && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowInvoices(false); }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '28px', width: '100%', maxWidth: '500px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: '#2D3277', margin: 0 }}>Histórico de Faturas</h3>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {invoices.length > 1 && (
                  <button onClick={downloadAll} style={{ background: 'none', border: '1px solid #2D3277', color: '#2D3277', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                    Baixar todas
                  </button>
                )}
                <button onClick={() => setShowInvoices(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#999', lineHeight: 1 }}>✕</button>
              </div>
            </div>

            <div style={{ overflowY: 'auto', flex: 1 }}>
              {invoicesLoading && <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>Carregando...</p>}
              {!invoicesLoading && invoices.length === 0 && <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>Nenhuma fatura encontrada.</p>}
              {!invoicesLoading && invoices.map((inv) => {
                const fmtDate = (d) => d ? d.split('T')[0].split('-').reverse().join('/') : '—';
                const isPaid = inv.status === 'paid';
                return (
                  <div key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <div>
                      <span style={{ fontSize: '0.78rem', color: '#888', display: 'block', marginBottom: '2px' }}>Fatura #{inv.id} · Venc. {fmtDate(inv.due_date)}</span>
                      <span style={{ fontSize: '0.95rem', fontWeight: 600, color: '#222' }}>R$ {Number(inv.amount).toFixed(2).replace('.', ',')}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: isPaid ? '#16a34a' : '#f59e0b', background: isPaid ? '#f0fff4' : '#fffbeb', padding: '3px 8px', borderRadius: '20px' }}>
                        {isPaid ? 'Pago' : 'Pendente'}
                      </span>
                      <button onClick={() => downloadReceipt(inv.id)} style={{ background: 'none', border: '1px solid #ddd', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', color: '#555' }}>
                        Baixar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  flex: 1, padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px',
  fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box'
};

const btnStyle = {
  padding: '8px 16px', backgroundColor: '#2D3277', color: 'white',
  border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem',
  fontWeight: 600, whiteSpace: 'nowrap'
};
